import { Component, ReactNode } from 'react'
import { withTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { Divider } from 'semantic-ui-react'
import Bowser from 'bowser'
import StackTrace from 'stacktrace-js'
import * as Sentry from '@sentry/browser'
import { Location } from 'react-router-dom'

import { ReactComponent as RefreshIcon } from '@/assets/images/icons/arrows/refresh-cw-01.svg'
import Button from '@/ui/Button'
import Modal from '@/ui/Modal'
import { CREATE_ERROR_REPORT } from '@/graphql/mutations/ErrorReport'
import { client } from '@/components/App/client'
import BugReportForm from './BugReportForm'

interface ErrorBoundaryProps {
  modal?: boolean
  location: Location
  children: ReactNode
  t: TFunction
}

interface ErrorBoundaryState {
  hasNewUpdate: boolean
  hasError: boolean
  errorStack: string | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state = {
    hasNewUpdate: false,
    hasError: false,
    errorStack: null,
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.props?.location?.pathname !== prevProps?.location?.pathname) {
      this.setState({
        errorStack: null,
      })
    }
  }

  static getDerivedStateFromError(e: Error) {
    console.log(e)
    return {
      hasNewUpdate:
        e.message.includes('Loading chunk') ||
        e.message.includes('Loading CSS chunk'),
    }
  }

  componentDidCatch(error: Error) {
    this.setState({ hasError: true })

    Sentry.captureException(error)

    StackTrace.fromError(error).then(stackframes => {
      const stringifiedStack = stackframes.map(sf => sf.toString()).join('\n')
      const errorStack = `${error.message}\n${stringifiedStack}`
      this.setState({ errorStack })

      if (
        !!sessionStorage.getItem('accessToken') &&
        process.env.NODE_ENV === 'production' &&
        !window.location.href.includes('beta-staff.hr-on.com') &&
        !this.state.hasNewUpdate
      ) {
        client.mutate({
          mutation: CREATE_ERROR_REPORT,
          variables: {
            isCrash: true,
            error: errorStack,
            routeName: window.location.href,
            userAgent: Bowser.parse(window.navigator.userAgent),
          },
        })
      }
    })
  }

  render() {
    const { modal, children, t } = this.props
    const { errorStack, hasNewUpdate } = this.state

    if (errorStack) {
      const errorContent = (
        <div
          style={{
            textAlign: 'center',
            maxWidth: '500px',
            margin: 'auto',
          }}>
          <BugReportForm
            errorVariables={{
              error: errorStack,
              routeName: window.location.href,
              userAgent: Bowser.parse(window.navigator.userAgent),
            }}
          />

          {hasNewUpdate && !modal && <Divider section />}

          {hasNewUpdate && (
            <p>
              {t(
                'HR-ON Staff has received a new update. Please refresh the page.',
              )}
            </p>
          )}

          {!modal && (
            <Button
              leftIcon={<RefreshIcon />}
              style={{ margin: 'auto' }}
              content={t('Refresh page')}
              onClick={() => window.location.reload()}
            />
          )}
        </div>
      )

      if (modal) {
        return (
          <Modal.Content>
            <Modal.Description style={{ width: '100%', padding: '40px 0' }}>
              {errorContent}
            </Modal.Description>
          </Modal.Content>
        )
      }

      return errorContent
    }

    if (this.state.hasError) {
      return null
    }

    return children
  }
}

export default withTranslation()(ErrorBoundary)
